import { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import { DRIVE_CONSTANTS } from "@/constants/Drive.constant";
import { driveTypes } from "@/constants/constant-values/default-values.constant";
import {
  EIGHTY,
  FIFTY,
  FIVE,
  FIVE_HUNDRED,
  HUNDRED,
  NINETY,
  ONE,
  ONE_KB,
  ONE_MB,
  SIXTY,
  TEN,
  THOUSAND,
  THREE,
  THREE_HUNDRED,
  TWENTY,
  TWO,
} from "@/constants/constants";
import { REQUEST_METHODS } from "@/constants/global-constants";
import { useDrive } from "@/context/DriveContext";
import { useNestedStructure } from "@/hooks/useNestedStructure";
import { BatchFileItem, BatchFolderItem } from "@/interfaces/project.interface";
import { useAlterLeadsDriveMutation } from "@/redux/api/prQuery";
import { useAlterProjectMutation } from "@/redux/api/projectsQuery";
import { RootState } from "@/redux/store";
import { customToast } from "@/utils/custom-toast";

export const useBatchUpload = (): {
  uploadBatch: (
    files: BatchFileItem[],
    folders: BatchFolderItem[],
    onProgress?: (progress: number) => void,
    onFilesUpdate?: (files: BatchFileItem[]) => void,
  ) => Promise<void>;
  isUploading: boolean;
  uploadProgress: number;
  uploadError: string | undefined;
  unsupportedFiles: string[];
  resetUpload: () => void;
} => {
  const {
    folderId,
    activeFolderTarget,
    type,
    ID: id,
    projectName,
    clientName,
    directoryPath,
  } = useDrive();
  const [alterProject] = useAlterProjectMutation();
  const [alterLeadsDrive] = useAlterLeadsDriveMutation();
  const { createNestedDirectoryStructure } = useNestedStructure();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);
  const [unsupportedFiles, setUnsupportedFiles] = useState<string[]>([]);

  const workspace = useSelector(
    (state: RootState) => state.user?.workspaceData,
  );
  const mutationHandler = useCallback(() => {
    if (type === driveTypes.PROJECT || type === driveTypes.TASK_AND_CLAIMS) {
      return alterProject;
    } else {
      return alterLeadsDrive;
    }
  }, [type, alterProject, alterLeadsDrive]);

  const uploadBatch = useCallback(
    async (
      files: BatchFileItem[],
      folders: BatchFolderItem[],
      onProgress?: (progress: number) => void,
      onFilesUpdate?: (files: BatchFileItem[]) => void,
    ): Promise<void> => {
      if (!files || files.length === 0) {
        customToast.info("No files to upload");

        return;
      }

      setIsUploading(true);
      setUploadError(undefined);
      setUploadProgress(0);

      try {
        const {
          structure,
          validFiles,
          hasUnsupportedFiles,
          unsupportedFileNames,
        } = createNestedDirectoryStructure(folders, files);

        setUnsupportedFiles(unsupportedFileNames);

        if (!structure) {
          setIsUploading(false);
          setUploadProgress(0);

          return;
        }

        const updatedBatchFiles = hasUnsupportedFiles
          ? validFiles.map((file) => ({
              ...file,
              status: "pending",
              progress: 0,
            }))
          : files;

        const generateFilePath = (
          fileName: string,
          folderPath: string[],
          projectName?: string,
          clientName?: string,
        ): string => {
          const workspaceName = workspace?.name;
          const currentYear = new Date().getFullYear().toString();
          let directoryPrefix = "";
          if (
            directoryPath &&
            Array.isArray(directoryPath) &&
            directoryPath.length > 0
          ) {
            // directoryPath is an array of objects with id and name
            directoryPrefix =
              directoryPath
                .map((dir) => dir.name)
                .filter(Boolean)
                .join("/") + "/";
          }
          // Build folder path from the upload structure
          const folderPathString =
            folderPath.length > 0 ? folderPath.join("/") + "/" : "";

          const fullPath = `${directoryPrefix}${folderPathString}`;
          if (type === driveTypes.TASK_AND_CLAIMS) {
            return `${workspaceName}/Task And Claims/Awarded Projects (${currentYear})/${projectName}/${fullPath}${fileName}`;
          } else if (type === driveTypes.PROJECT_REGISTER) {
            return `${workspaceName}/Project Register/${currentYear} Projects/${clientName}/${projectName}/${fullPath}${fileName}`;
          } else {
            // PROJECT
            return `${workspaceName}/Site Folder/Ongoing Projects ${currentYear}/${projectName}/${fullPath}${fileName}`;
          }
        };
        const formData = new FormData();
        const targetFolderId = activeFolderTarget || folderId;
        const structureWithPaths = {
          ...structure,
          files: validFiles.map((fileItem) => ({
            ...fileItem,
            name: fileItem.file.name,
            size: fileItem.file.size,
            s3Path: generateFilePath(
              fileItem.file.name,
              fileItem.path || [],
              projectName,
              clientName,
            ),
          })),
        };
        formData.append("structure", JSON.stringify(structureWithPaths));
        validFiles.forEach((fileItem) => {
          formData.append("files", fileItem.file);
        });

        if (targetFolderId) {
          formData.append("parentFolderId", targetFolderId);
        }

        formData.append(
          "driveType",
          ((): string => {
            if (type === driveTypes.PROJECT) {
              return driveTypes.PROJECT;
            } else if (type === driveTypes.TASK_AND_CLAIMS) {
              return driveTypes.TASK_AND_CLAIMS;
            } else {
              return driveTypes.PROJECT_REGISTER;
            }
          })(),
        );

        const totalBytes = updatedBatchFiles.reduce(
          (total, file) => total + file.file.size,
          0,
        );

        const folderDepthFactor = Math.max(
          ...folders.map((f) => f.path.length),
          0,
        );
        const totalItems = files.length + folders.length;

        const isVeryLargeUpload =
          totalBytes > HUNDRED * ONE_MB ||
          folderDepthFactor > THREE ||
          totalItems > HUNDRED;

        // Update files to uploading status
        const uploadingFiles = updatedBatchFiles.map((file) => ({
          ...file,
          status: "uploading" as const,
          progress: 0,
        }));
        onFilesUpdate?.(uploadingFiles);

        // Setup progress tracking
        let progressInterval: NodeJS.Timeout | null = null;
        let progressCounter = 0;
        let slowUploadWarningShown = false;

        // More realistic timing calculation based on file size
        const estimatedUploadTimeSeconds = Math.max(
          TWO, // Minimum 2 seconds even for very small files
          Math.min(
            SIXTY, // Maximum 60 seconds for simulation
            Math.ceil(totalBytes / (FIVE * ONE_MB)) + // 5MB per second base speed
              folderDepthFactor * TWO + // 2 seconds per folder depth level
              Math.ceil(totalItems / FIFTY), // 50 items per second processing
          ),
        );

        // Faster progress updates for smaller files
        const progressIncrementDelay = Math.max(
          HUNDRED, // Minimum 100ms updates
          Math.min(
            THOUSAND, // Maximum 1 second updates
            Math.floor((estimatedUploadTimeSeconds * THOUSAND) / FIFTY), // 50 updates total
          ),
        );

        // Dynamic increment based on file size and estimated time
        const getProgressIncrement = (currentProgress: number): number => {
          if (totalBytes < ONE_MB) {
            // Very small files - progress faster
            if (currentProgress < FIFTY) {
              return TEN;
            }
            if (currentProgress < EIGHTY) {
              return FIVE;
            }

            return TWO;
          } else if (totalBytes < TEN * ONE_MB) {
            // Medium files - normal speed
            if (currentProgress < SIXTY) {
              return FIVE;
            }
            if (currentProgress < EIGHTY) {
              return THREE;
            }

            return ONE;
          } else {
            // Large files - slower progression
            if (currentProgress < SIXTY) {
              return THREE;
            }
            if (currentProgress < EIGHTY) {
              return TWO;
            }

            return ONE;
          }
        };

        progressInterval = setInterval(() => {
          if (progressCounter < NINETY) {
            const increment = getProgressIncrement(progressCounter);
            progressCounter = Math.min(NINETY, progressCounter + increment);
            setUploadProgress(progressCounter);
            onProgress?.(progressCounter);

            if (
              isVeryLargeUpload &&
              progressCounter > FIFTY &&
              !slowUploadWarningShown
            ) {
              customToast.info(DRIVE_CONSTANTS.MESSAGES.COMPLEX_UPLOAD, {
                duration: HUNDRED,
                position: "bottom-left",
              });
              slowUploadWarningShown = true;
            }
          }
        }, progressIncrementDelay);

        // Reduced timeout for smaller files
        const timeoutDuration = Math.max(
          SIXTY * THOUSAND, // Minimum 60 seconds
          Math.min(
            THREE_HUNDRED * THOUSAND, // Maximum 300 seconds
            totalBytes / (ONE_KB * TEN) + // 10KB per second base timeout
              folderDepthFactor * TWENTY * THOUSAND + // 20 seconds per folder depth
              totalItems * FIVE_HUNDRED, // 500ms per item
          ),
        );

        const mutation = mutationHandler();
        const res = await mutation({
          ...(type === driveTypes.PROJECT_REGISTER
            ? { leadsId: id }
            : { projectId: id }),
          data: formData,
          method: REQUEST_METHODS.POST,
          params: "drive/batch-upload-structure",
          timeout: timeoutDuration,
        });

        if (progressInterval) {
          clearInterval(progressInterval);
          progressInterval = null;
        }

        if (res?.data) {
          setUploadProgress(HUNDRED);
          onProgress?.(HUNDRED);

          const successFiles = updatedBatchFiles.map((file) => ({
            ...file,
            status: "success" as const,
            progress: HUNDRED,
          }));
          onFilesUpdate?.(successFiles);

          customToast.success(DRIVE_CONSTANTS.MESSAGES.BATCH_UPLOAD_SUCCESS, {
            position: "bottom-left",
          });
        } else {
          const failedFiles = updatedBatchFiles.map((file) => ({
            ...file,
            status: "error" as const,
            progress: 0,
          }));
          onFilesUpdate?.(failedFiles);
        }
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [
      activeFolderTarget,
      clientName,
      createNestedDirectoryStructure,
      directoryPath,
      folderId,
      id,
      mutationHandler,
      projectName,
      type,
      workspace?.name,
    ],
  );

  const resetUpload = useCallback(() => {
    setUploadProgress(0);
    setUploadError(undefined);
    setUnsupportedFiles([]);
    setIsUploading(false);
  }, []);

  return {
    uploadBatch,
    isUploading,
    uploadProgress,
    uploadError,
    unsupportedFiles,
    resetUpload,
  };
};
